// Projective homography core — dependency-free ES module (ADR-0082, article #177).
//
// A homography is the 3×3 matrix that maps one quadrilateral onto another in the
// plane. WIN uses it to STRAIGHTEN a perspective-warped crop: the source quad
// (four corners the annotator clicked, in frame-relative pixel space) is mapped
// onto its own axis-aligned bounding rect (the crop framing), producing a single
// CSS `matrix3d` that both un-warps and frames the image in one transform.
//
// Used by BOTH surfaces:
//   - the authoring controller (image_annotation_controller.js), for the live
//     straightened gallery preview, and
//   - the consumer render (image_crop_homography_controller.js), which reads the
//     persisted quad and applies the matrix3d on lightbox / PWA / decks.
//
// CRITICAL CSS RULE: a `matrix3d` produced here is computed in frame-relative
// PIXEL coordinates, so the element it is applied to MUST set
// `transform-origin: 0 0`. With no matrix3d (axis-aligned crop), origin stays
// `center center`. Getting the origin wrong silently skews the result.
//
// The maths is the verbatim prototype from article #177 — kept identical, only
// `var` → `const`/`let` and JSDoc added. Do not "tidy" the algebra.

/**
 * Solve the linear system A·x = b by Gaussian elimination with partial pivoting.
 * @param {number[][]} A - n×n coefficient matrix (row-major).
 * @param {number[]} b - length-n right-hand side.
 * @returns {number[]} x - the length-n solution vector.
 */
export function gaussElim(A, b) {
  const n = b.length;
  const M = A.map((r, i) => r.concat([b[i]]));
  for (let c = 0; c < n; c++) {
    let mx = c;
    for (let r = c + 1; r < n; r++) if (Math.abs(M[r][c]) > Math.abs(M[mx][c])) mx = r;
    const tmp = M[c]; M[c] = M[mx]; M[mx] = tmp;
    for (let r2 = c + 1; r2 < n; r2++) {
      const f = M[r2][c] / M[c][c];
      for (let j = c; j <= n; j++) M[r2][j] -= f * M[c][j];
    }
  }
  const x = new Array(n).fill(0);
  for (let i = n - 1; i >= 0; i--) {
    x[i] = M[i][n];
    for (let j2 = i + 1; j2 < n; j2++) x[i] -= M[i][j2] * x[j2];
    x[i] /= M[i][i];
  }
  return x;
}

/**
 * Compute the 3×3 projective homography mapping `src` corners onto `dst` corners.
 * Both arrays are length-4 lists of {x, y} points in the SAME (pixel) space,
 * corresponding index-for-index (TL→TR→BR→BL by WIN convention).
 * @param {{x:number, y:number}[]} src - source quad, length 4.
 * @param {{x:number, y:number}[]} dst - destination quad, length 4.
 * @returns {number[]} the 9 homography entries [h0..h8] (row-major, h8 fixed at 1).
 */
export function computeH(src, dst) {
  const A = [];
  const b = [];
  for (let i = 0; i < 4; i++) {
    const sx = src[i].x, sy = src[i].y, dx = dst[i].x, dy = dst[i].y;
    A.push([-sx, -sy, -1, 0, 0, 0, sx * dx, sy * dx]); b.push(-dx);
    A.push([0, 0, 0, -sx, -sy, -1, sx * dy, sy * dy]); b.push(-dy);
  }
  const h = gaussElim(A, b);
  return [h[0], h[1], h[2], h[3], h[4], h[5], h[6], h[7], 1];
}

/**
 * Serialise a homography (9 entries, row-major) into a CSS `matrix3d(...)` string.
 * matrix3d is column-major and 4×4; the planar homography is embedded with the
 * z-row/column as identity. Apply the returned string with `transform-origin: 0 0`.
 * @param {number[]} H - 9 homography entries [h0..h8].
 * @returns {string} a `matrix3d(...)` CSS transform value.
 */
export function hToCSS(H) {
  const h0 = H[0], h1 = H[1], h2 = H[2], h3 = H[3], h4 = H[4], h5 = H[5], h6 = H[6], h7 = H[7], h8 = H[8];
  return 'matrix3d(' +
    h0 + ',' + h3 + ',0,' + h6 + ',' +
    h1 + ',' + h4 + ',0,' + h7 + ',' +
    '0,0,1,0,' +
    h2 + ',' + h5 + ',0,' + h8 + ')';
}

/**
 * The content rectangle of an image rendered under `object-fit: contain` inside a
 * frame. Returns the offset (x, y) and size (w, h) of the actual pixels plus the
 * contain scale. This rect is the destination space for the straightening
 * homography: its four corners are the crop framing.
 * @param {Element} frame - the clipping container (clientWidth/clientHeight).
 * @param {HTMLImageElement} img - the image (naturalWidth/naturalHeight).
 * @returns {{x:number, y:number, w:number, h:number, scale:number}}
 */
export function getImgRect(frame, img) {
  const fw = frame.clientWidth, fh = frame.clientHeight;
  const nw = img.naturalWidth, nh = img.naturalHeight;
  if (!nw || !nh) return { x: 0, y: 0, w: fw, h: fh, scale: 1 };
  const sc = Math.min(fw / nw, fh / nh);
  const w = nw * sc, h = nh * sc;
  return { x: (fw - w) / 2, y: (fh - h) / 2, w: w, h: h, scale: sc };
}

// --- Shared crop-render helpers ---------------------------------------------
// Used by every consumer that straightens a PERSPECTIVE crop quad (the lightbox,
// the PWA viewer, the authoring gallery toggle): one place owns the "quad →
// matrix3d for this frame" maths so the surfaces never duplicate it. ADR-0082.

/**
 * Reorder a quad's four corners into canonical TL, TR, BR, BL order, regardless
 * of the order they were clicked/stored in. The polygon picker records click
 * order verbatim — a user who clicks DOWN the left side first stores TL, BL, BR,
 * TR — and `cropMatrix3d` maps corners by INDEX onto the destination rect, so an
 * out-of-order quad reflects/rotates the straighten. Normalising here makes the
 * source order irrelevant.
 *
 * Method (the sum/diff trick, robust for convex shelf quads): with s = x + y and
 * d = x − y over the four points, TL is argmin(s) (smallest x+y, top-left), BR is
 * argmax(s) (largest x+y, bottom-right), TR is argmax(d) (largest x−y), and BL is
 * argmin(d) (smallest x−y). Pure geometry, no winding assumptions.
 * @param {Array<[number, number]>} quad - four [x, y] points in any order.
 * @returns {Array<[number, number]>} the same points as TL, TR, BR, BL.
 */
export function orderQuadCorners(quad) {
  if (!Array.isArray(quad) || quad.length !== 4) return quad;

  let tl = quad[0], tr = quad[0], br = quad[0], bl = quad[0];
  let minS = Infinity, maxS = -Infinity, maxD = -Infinity, minD = Infinity;
  for (const p of quad) {
    const s = p[0] + p[1];
    const d = p[0] - p[1];
    if (s < minS) { minS = s; tl = p; }
    if (s > maxS) { maxS = s; br = p; }
    if (d > maxD) { maxD = d; tr = p; }
    if (d < minD) { minD = d; bl = p; }
  }
  return [tl, tr, br, bl];
}

/**
 * Solve the `matrix3d` CSS string that straightens AND frames a perspective crop
 * quad into the image's content rect (the destination IS the crop framing). The
 * caller applies the returned string to the <img> with `transform-origin: 0 0`.
 * Returns null when the geometry is not yet measurable (e.g. natural size unknown)
 * so the caller can retry on load/resize. The quad is canonicalised to TL,TR,BR,BL
 * first, so click/storage order never reflects or rotates the result.
 * @param {Element} frame - the clipping container the image sits in.
 * @param {HTMLImageElement} img - the image being straightened.
 * @param {Array<[number, number]>} quad - normalised 0–1 corners, any order.
 * @returns {string|null} a `matrix3d(...)` string, or null if not solvable.
 */
export function cropMatrix3d(frame, img, quad) {
  if (!Array.isArray(quad) || quad.length !== 4) return null;
  const rect = getImgRect(frame, img);
  if (!rect.w || !rect.h) return null;

  const ordered = orderQuadCorners(quad);
  const src = ordered.map((p) => ({ x: rect.x + p[0] * rect.w, y: rect.y + p[1] * rect.h }));
  const dst = [
    { x: rect.x, y: rect.y },
    { x: rect.x + rect.w, y: rect.y },
    { x: rect.x + rect.w, y: rect.y + rect.h },
    { x: rect.x, y: rect.y + rect.h }
  ];
  return hToCSS(computeH(src, dst));
}
